import { useLocation } from "react-router-dom"
import { useEffect, useMemo, useState } from "react"
import { BASE_ROUTE } from "../../../routes"
import { useSelector } from "react-redux"
import { selectIsUserOnboarded } from "../../../domain/Practices/selectors"
import {
  DEEP_LINK_QUERY_KEY,
  MSAL_DEEP_LINK_URL,
} from "../../dataFetchingRouter/helpers"

// Enum for allowed deep link targets. Extend as needed.
enum DeepLinkTargetEnum {
  PRACTICE_AGREEMENT = "practiceAgreement",
  // Add more allowed values here
}

type DeepLinkCallbackMap = Record<DeepLinkTargetEnum, () => string | void>

/**
 * Self-contained hook to handle deep link localStorage and query param logic.
 * Returns { complete, finalUrl }.
 */
export const useDeepLinkHandler = (
  readyToExecute: boolean,
): {
  complete: boolean
  finalUrl: string | null
} => {
  const location = useLocation()
  const [complete, setComplete] = useState(false)
  const [finalUrl, setFinalUrl] = useState<string | null>(null)
  const isUserOnboarded = useSelector(selectIsUserOnboarded)

  // Define callbacks for each allowed deep link target
  // Note: returning a new url is optional, if not provided the original url will be used
  const callbacks: DeepLinkCallbackMap = useMemo(
    () => ({
      [DeepLinkTargetEnum.PRACTICE_AGREEMENT]: () => {
        // Dont let the user deep link if theyre trying to reach the agreement via the stale
        // practice agreement deep link and they are not onboarded
        if (!isUserOnboarded) {
          return BASE_ROUTE
        }
      },
      // Extend with more deep link targets as needed
    }),
    [isUserOnboarded],
  )

  useEffect(() => {
    // Early return if initial data fetch is not complete
    if (!readyToExecute) {
      return
    }

    // 1. Check for deep link in localStorage (B2C scenario)
    const storedUrl = localStorage.getItem(MSAL_DEEP_LINK_URL)
    let urlToUse = storedUrl
    // 2. If not in localStorage, use the base route
    // Note: if no deep link is present in localStorage, then the user accessed the app directly
    // and we are safe to default to the base route
    if (!urlToUse) {
      urlToUse = BASE_ROUTE
    }
    // 3. Parse for deep link query param
    const params = new URLSearchParams(urlToUse.split("?")[1])
    const target = params.get(DEEP_LINK_QUERY_KEY)
    let newUrl = urlToUse
    if (
      target &&
      Object.values(DeepLinkTargetEnum).includes(target as DeepLinkTargetEnum)
    ) {
      const cb = callbacks[target as DeepLinkTargetEnum]
      if (cb) {
        const result = cb()
        if (typeof result === "string") {
          newUrl = result
        }
      }
    }
    // 4. Clean up localStorage and remove the deep link query param
    localStorage.removeItem(MSAL_DEEP_LINK_URL)
    if (newUrl.includes("?") && newUrl.includes(DEEP_LINK_QUERY_KEY)) {
      const [base, query] = newUrl.split("?")
      const params = new URLSearchParams(query)
      params.delete(DEEP_LINK_QUERY_KEY)
      newUrl = params.toString() ? `${base}?${params}` : base
    }
    setFinalUrl(newUrl)
    setComplete(true)
  }, [callbacks, location.pathname, location.search, readyToExecute])

  return { complete, finalUrl }
}
